import { useSyncExternalStore } from "react";

/**
 * Where the access token lives — and, just as importantly, where it does not.
 *
 * (H) doc 23 §11a: "Tokens are held in memory with a refresh cookie that is
 * `HttpOnly`, `Secure` and `SameSite=Strict`. **No access token in
 * `localStorage`**". So this is a module-level variable and nothing else. A
 * reload ends the session, which is the intended trade: an XSS yields a
 * page-lifetime credential rather than a durable one.
 *
 * The active practice id *is* persisted, in `sessionStorage`. It is a UI
 * selection, not a credential — the API validates it against the caller's
 * grants on every request (doc 22 §11), so a tampered value buys nothing.
 */

export interface Credentials {
  accessToken: string;
  /** RFC 3339. Used to show the session's remaining life, never to trust it. */
  expiresAt: string;
  actorId: string;
  tenantId: string;
  sessionId: string;
  /**
   * What the user typed at sign-in.
   *
   * It is kept because `GET /me` carries no display name and no email —
   * `ActorIdentity` is an id, a kind and a tenant — so without this the account
   * menu can only show an opaque UUID. It is a label, never an assertion: the
   * API decides who the caller is from the token, not from this string.
   *
   * (H) `null` on a session restored after a page reload, and that is the honest
   * value. The refresh cookie carries a session id and the token carries no
   * email, so this path genuinely does not know it — and the account menu says
   * "Signed in" rather than inventing a name from the actor id.
   */
  email: string | null;
}

export type SignedOutReason = "user" | "expired" | null;

interface SessionState {
  credentials: Credentials | null;
  /** Why the last sign-out happened, so the sign-in screen can say so. */
  signedOutReason: SignedOutReason;
  /** `X-Sarva-Practice-Id`. `null` means "whatever the identity defaults to". */
  practiceId: string | null;
}

const PRACTICE_STORAGE_KEY = "sarva.active-practice-id";

/**
 * (H) The selection is stored **with the actor who made it**. Stored alone, it outlived the
 * person: a second user signing in on the same tab (a shared front-desk workstation) inherited
 * the first user's practice, every request carried a practice outside their grants, and the
 * whole application answered "Outside your access scope" with a Try again that could not help.
 * Found by the QA pass on dev, 2026-11-02.
 */
interface StoredPractice {
  actorId: string | null;
  practiceId: string;
}

function readStored(): StoredPractice | null {
  try {
    const raw = globalThis.sessionStorage?.getItem?.(PRACTICE_STORAGE_KEY) ?? null;
    if (raw === null) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<StoredPractice>;
      if (typeof parsed.practiceId === "string") {
        return { actorId: parsed.actorId ?? null, practiceId: parsed.practiceId };
      }
    } catch {
      // The earlier format: a bare id with no owner. Kept for a reload, dropped at sign-in.
    }
    return { actorId: null, practiceId: raw };
  } catch {
    // A hardened profile or Safari private mode. A practice selection is never
    // worth taking the application down for.
    return null;
  }
}

function readStoredPracticeId(): string | null {
  return readStored()?.practiceId ?? null;
}

function writeStoredPracticeId(value: string | null, actorId: string | null): void {
  try {
    if (value === null) globalThis.sessionStorage?.removeItem?.(PRACTICE_STORAGE_KEY);
    else
      globalThis.sessionStorage?.setItem?.(
        PRACTICE_STORAGE_KEY,
        JSON.stringify({ actorId, practiceId: value } satisfies StoredPractice),
      );
  } catch {
    // See above.
  }
}

let state: SessionState = {
  credentials: null,
  signedOutReason: null,
  practiceId: readStoredPracticeId(),
};

const listeners = new Set<() => void>();

function set(next: Partial<SessionState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Read-only snapshot. Stable identity between changes, as `useSyncExternalStore` requires. */
export function getSessionState(): SessionState {
  return state;
}

export function signIn(credentials: Credentials): void {
  // A practice chosen by somebody else is not this person's selection (see `StoredPractice`).
  const stored = readStored();
  if (stored !== null && stored.actorId !== credentials.actorId) {
    writeStoredPracticeId(null, null);
    set({ credentials, signedOutReason: null, practiceId: null });
    return;
  }
  set({ credentials, signedOutReason: null });
}

/**
 * Drop the credential.
 *
 * `reason` is what the sign-in screen shows: a session that expired under the
 * user says something different from one they ended themselves (doc 41 §6 —
 * say what happened and what to do).
 */
export function signOut(reason: Exclude<SignedOutReason, null> = "user"): void {
  set({ credentials: null, signedOutReason: reason });
}

export function clearSignedOutReason(): void {
  if (state.signedOutReason !== null) set({ signedOutReason: null });
}

export function setActivePracticeId(practiceId: string | null): void {
  writeStoredPracticeId(practiceId, state.credentials?.actorId ?? null);
  set({ practiceId });
}

/** The transport's per-request accessors (see `shared/api/client.ts`). */
export const readAccessToken = (): string | null => state.credentials?.accessToken ?? null;
export const readPracticeId = (): string | null => state.practiceId;

export function useSessionState(): SessionState {
  return useSyncExternalStore(subscribe, getSessionState, getSessionState);
}

/** Test seam: reset to a signed-out store between tests. */
export function resetSessionStore(): void {
  writeStoredPracticeId(null, null);
  set({ credentials: null, signedOutReason: null, practiceId: null });
}
